/**
 * Types for the Care Block Detail drawer — the inner view behind every card in
 * the careOS Care Guide kanban (`PatientProfile` → Overview → Care Guide).
 *
 * The drawer answers two questions about one block: what has happened on it
 * (an activity feed mixing care-team and member events), and what does the care
 * team want to record about it now (patient-reported feedback capture).
 *
 * Prototype shapes, like the rest of `careOSPrototype.ts` — they back the mock
 * layer, not a real backend contract. Kept in their own file rather than folded
 * into `careOSPrototype.ts` because the drawer is its own feature with its own
 * vocabulary (feedback answers, activity kinds); that file is already the
 * catch-all for the profile + labs surface.
 */

// ─── Feedback answers ───────────────────────────────────────────────────────
// Two independent axes, each captured as a single-select row in the drawer's
// footer form. They're separate unions rather than one flat enum because the
// UI enforces "at most one selection per row" — the row *is* the type.

export type SymptomsFeedback =
  | 'major-improvement'
  | 'noticeable-improvement'
  | 'no-change'
  | 'worsening';

export type ImplementationFeedback =
  | 'successful'
  | 'challenges'
  | 'not-attempted';

export type FeedbackAnswer = SymptomsFeedback | ImplementationFeedback;

/**
 * Drives color coding in both places an answer appears. Positive answers read
 * Seafoam when selected in the form and Mint once submitted to the feed;
 * negative answers read Plum and Ballet respectively. The valence lives on the
 * answer (not on the component) so the two surfaces can't drift apart.
 */
export type FeedbackValence = 'positive' | 'negative';

/**
 * One label per answer, shared by the form's pills, the feed's tags, and the
 * kanban card. The feed once used longer stand-alone labels ("Noticeable
 * symptom improvement"); production shows the short ones, so careOS does too.
 */
export interface FeedbackAnswerMeta {
  formLabel: string;
  valence: FeedbackValence;
}

export const SYMPTOMS_FEEDBACK_META: Record<SymptomsFeedback, FeedbackAnswerMeta> = {
  'major-improvement': {
    formLabel: 'Major improvement',
    valence: 'positive',
  },
  'noticeable-improvement': {
    formLabel: 'Noticeable improvement',
    valence: 'positive',
  },
  'no-change': {
    formLabel: 'No change',
    valence: 'negative',
  },
  worsening: {
    formLabel: 'Worsening',
    valence: 'negative',
  },
};

export const IMPLEMENTATION_FEEDBACK_META: Record<
  ImplementationFeedback,
  FeedbackAnswerMeta
> = {
  successful: {
    formLabel: 'Successful',
    valence: 'positive',
  },
  challenges: {
    formLabel: 'Challenges',
    valence: 'negative',
  },
  'not-attempted': {
    formLabel: 'Not attempted',
    valence: 'negative',
  },
};

// Render order for each row. Explicit arrays rather than `Object.keys` so the
// order is a design decision in source, not an artifact of object literal order.
export const SYMPTOMS_FEEDBACK_ORDER: SymptomsFeedback[] = [
  'major-improvement',
  'noticeable-improvement',
  'no-change',
  'worsening',
];

export const IMPLEMENTATION_FEEDBACK_ORDER: ImplementationFeedback[] = [
  'successful',
  'challenges',
  'not-attempted',
];

export const feedbackAnswerMeta = (answer: FeedbackAnswer): FeedbackAnswerMeta =>
  answer in SYMPTOMS_FEEDBACK_META
    ? SYMPTOMS_FEEDBACK_META[answer as SymptomsFeedback]
    : IMPLEMENTATION_FEEDBACK_META[answer as ImplementationFeedback];

// ─── Member app check-ins ───────────────────────────────────────────────────
// The member's own "how's it going" answers, submitted on a goal from the
// mobile app and surfaced here alongside care-team activity.
//
// PLACEHOLDER SET: the design spec only showed "It's not helping". The real
// options are whatever the app ships today — replace this union (and the meta
// below) with that list rather than treating these four as authoritative.

export type AppCheckInResponse =
  | 'going-well'
  | 'somewhat-helping'
  | 'not-helping'
  | 'havent-started';

export const APP_CHECK_IN_META: Record<
  AppCheckInResponse,
  { label: string; valence: FeedbackValence }
> = {
  'going-well': { label: "It's going well", valence: 'positive' },
  'somewhat-helping': { label: "It's somewhat helping", valence: 'positive' },
  'not-helping': { label: "It's not helping", valence: 'negative' },
  'havent-started': { label: "I haven't started", valence: 'negative' },
};

// ─── Activity feed ──────────────────────────────────────────────────────────
// Exactly four kinds. Anything else a block does (created, archived, nudged,
// streak lapsed) is intentionally out of the feed for now — the drawer is a
// record of *instructions and the feedback on them*, not a full audit log.
//
// `instructions` entries partition the feed: each one opens a period that runs
// until the next, and the feedback between them is feedback on that version.
// A review given at the same visit that rewrites the instructions is about the
// version being replaced, so it sits *before* the new `instructions` entry.

/** Whoever produced a feed entry — a clinician, a coach, or the member. */
export interface CareBlockActor {
  name: string;
  /**
   * Optional photo. The careOS surface has no avatar assets yet, so entries
   * fall back to an initial circle; wire real images here when they exist.
   */
  avatarUrl?: string;
}

interface CareBlockActivityBase {
  id: string;
  /** Display-only, e.g. "Feb 6". The prototype never sorts on this. */
  date: string;
}

/** Care-team-submitted patient-reported feedback — what the footer form writes. */
export interface CareTeamFeedbackActivity extends CareBlockActivityBase {
  kind: 'care-team-feedback';
  author: CareBlockActor;
  // Both optional: the form only requires one selection across both rows, so a
  // submission can carry a symptoms answer, an implementation answer, or both.
  symptoms?: SymptomsFeedback;
  implementation?: ImplementationFeedback;
  note?: string;
  /** Renders the "SOAP Note" affordance on the entry. Non-navigating for now. */
  hasSoapNote?: boolean;
}

/**
 * The block's instruction line was set — the original assignment or a later
 * rewrite. `date` is the period's start; its end is the next `instructions`
 * entry's date, or the present when there is none.
 */
export interface InstructionsActivity extends CareBlockActivityBase {
  kind: 'instructions';
  author: CareBlockActor;
  /** The full instruction text for this period — not a diff. */
  instruction: string;
}

/**
 * The member answered "how's it going" on this block in the mobile app.
 *
 * Carries no actor: the member is always the patient whose profile you're
 * looking at, so the view supplies the name. Storing it on the entry would let
 * a fixture's name contradict the open profile's.
 */
export interface AppCheckInActivity extends CareBlockActivityBase {
  kind: 'app-check-in';
  response: AppCheckInResponse;
}

/** Where an Ayble AI entry was read from — names its chart-note button. */
export type AiInsightSource = 'soap-note' | 'transcript';

/**
 * An entry Ayble's LLM drafted from a visit's chart note or transcript.
 *
 * `pending` drafts wait for a care team thumbs up before they count; a
 * thumbs down removes them with a reason. Carries no symptoms or
 * implementation answer on purpose — the model writes the note, and only a
 * care team submission moves the goal's status.
 */
export interface AiInsightActivity extends CareBlockActivityBase {
  kind: 'ai-insight';
  /** The visit it was read from, e.g. "Follow-up visit with Coach Carol". */
  visitLabel: string;
  source: AiInsightSource;
  note: string;
  /** The member's own words, when the source has them. */
  quote?: string;
  status: 'pending' | 'accepted';
  /** Who gave the thumbs up. Set once `status` is `accepted`. */
  acceptedBy?: CareBlockActor;
}

/** Why a care team member removed an Ayble AI draft. */
export type AiInsightRemovalReason = 'not-specific' | 'inaccurate' | 'missing-big-picture';

export const AI_INSIGHT_REMOVAL_REASONS: { value: AiInsightRemovalReason; label: string }[] = [
  { value: 'not-specific', label: 'Not specific enough' },
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'missing-big-picture', label: 'Missing big picture' },
];

export type CareBlockActivity =
  | CareTeamFeedbackActivity
  | InstructionsActivity
  | AppCheckInActivity
  | AiInsightActivity;

// ─── Block detail payload ───────────────────────────────────────────────────

export interface CareBlockDetail {
  id: string;
  title: string;
  /** The current instruction line, shown under the title in the drawer header. */
  instruction: string;
  /**
   * Shows "Mark Complete" beside Archive in the drawer header. Only the
   * Uncovered Goals entry sets it today; Care Guide blocks don't offer it.
   */
  canMarkComplete?: boolean;
  /**
   * Count behind the header's award badge — the same engagement metric the
   * kanban card's engagement addon summarizes. Omitted when the block has no
   * engagement signal, in which case the badge is not rendered.
   */
  engagementCount?: number;
  /**
   * Oldest first — the feed reads chronologically top to bottom, so the newest
   * entry is last (the drawer reverses it for display). Order is purely
   * positional: `date` is a display string and is never parsed or sorted on.
   * Starts with the original `instructions` entry once the block is assigned;
   * empty for a block nobody has set up yet.
   */
  activity: CareBlockActivity[];
}

/** Request body for POST …/feedback. At least one answer is required. */
export interface CareBlockFeedbackSubmission {
  symptoms?: SymptomsFeedback;
  implementation?: ImplementationFeedback;
  note?: string;
}
